import { Controller } from '@hotwired/stimulus'

const escape = value => String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]))
const text = value => new DOMParser().parseFromString(String(value || ''), 'text/html').body.textContent || ''
const date = value => value ? new Date(value).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'
const number = value => Number(value).toLocaleString('en-CA')

export default class extends Controller {
  static values = { base: String, categoriesUrl: String }
  static targets = ['stats', 'notice', 'categoryNav', 'search', 'sort', 'rows', 'dialog', 'kicker', 'title', 'form', 'wordCount', 'visibilityLabel', 'metadata', 'titleCount', 'descriptionCount', 'meta', 'drawerNotice', 'normalFooter', 'deleteConfirm', 'deleteButton', 'duplicateButton', 'saveButton', 'categoryDialog', 'categoryRows', 'newCategory', 'categoryNotice', 'toast', 'toastMessage', 'undoButton']

  connect() {
    this.connected = true
    this.questions = []
    this.categories = []
    this.activeCategory = ''
    this.activeTab = 'all'
    this.load()
  }

  disconnect() {
    this.connected = false
    this.loading?.abort()
    clearTimeout(this.toastTimer)
    this.beforeCache()
  }

  beforeCache() {
    this.dialogTarget.close()
    this.categoryDialogTarget.close()
    this.toastTarget.hidden = true
  }

  async request(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin', cache: 'no-store', ...options,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '' }
    })
    if (response.redirected || !response.headers.get('content-type')?.includes('application/json')) throw new Error('Your session may have expired. Refresh and sign in before continuing.')
    const data = await response.json()
    if (!response.ok) throw new Error(data.error || 'Could not save this change. Please try again.')
    return data
  }

  async load() {
    this.loading?.abort()
    const request = this.loading = new AbortController()
    try {
      const data = await this.request(`${this.baseValue}/data`, { signal: request.signal })
      if (!this.connected || request.signal.aborted) return
      this.setData(data)
      this.loaded = true
      this.render()
    } catch (error) {
      if (error.name !== 'AbortError' && this.connected) {
        this.showError(this.noticeTarget, error)
        this.rowsTarget.innerHTML = '<tr><td colspan="4" class="fq-empty">Questions could not be loaded. Please refresh to try again.</td></tr>'
      }
    }
  }

  setData(data) {
    this.questions = data.questions
    this.categories = data.categories
    if (this.activeCategory && !this.categories.some(category => String(category.id) === this.activeCategory)) this.activeCategory = ''
  }

  render() {
    const visible = this.questions.filter(question => question.is_visible).length
    const stats = [['Questions', this.questions.length, 'quiz', 'all'], ['Visible', visible, 'visibility', 'visible'], ['Hidden', this.questions.length - visible, 'visibility_off', 'hidden'], ['Categories', this.categories.length, 'category', 'categories']]
    this.statsTarget.innerHTML = stats.map(([label, value, icon, kind]) => `<div class="fq-stat fq-stat--${kind}"><div class="fq-stat-icon"><span class="material-symbols-rounded" aria-hidden="true">${icon}</span></div><div><small>${label}</small><strong>${number(value)}</strong></div></div>`).join('')
    this.categoryNavTarget.innerHTML = '<div class="fq-cat-heading">Categories</div>' + [{ id: '', name: 'All questions' }, ...this.categories].map(category => {
      const count = category.id === '' ? this.questions.length : this.categoryCount(category.id)
      return `<button type="button" data-category="${escape(category.id)}" aria-pressed="${String(category.id) === this.activeCategory}" data-action="admin-faqs#filterCategory">${escape(category.name)}<span>${number(count)}</span></button>`
    }).join('')
    const categoryQuestions = this.questions.filter(question => !this.activeCategory || String(question.category_id) === this.activeCategory)
    const counts = { all: categoryQuestions.length, visible: categoryQuestions.filter(question => question.is_visible).length, hidden: categoryQuestions.filter(question => !question.is_visible).length }
    this.element.querySelectorAll('[data-tab]').forEach(button => {
      button.setAttribute('aria-pressed', button.dataset.tab === this.activeTab)
      button.querySelector('[data-count]').textContent = number(counts[button.dataset.tab])
    })
    const query = this.searchTarget.value.trim().toLocaleLowerCase()
    const rows = categoryQuestions.filter(question => (this.activeTab === 'all' || question.is_visible === (this.activeTab === 'visible')) && (!query || text(`${question.question} ${question.answer}`).toLocaleLowerCase().includes(query)))
    rows.sort((a, b) => this.sortTarget.value === 'az' ? text(a.question).localeCompare(text(b.question)) : this.sortTarget.value === 'id' ? a.id - b.id : String(b.last_updated || '').localeCompare(String(a.last_updated || '')) || b.id - a.id)
    this.rowsTarget.innerHTML = rows.map(question => `<tr data-id="${question.id}" class="${question.is_visible ? '' : 'is-hidden'} ${this.question?.id === question.id && this.dialogTarget.open ? 'is-editing' : ''}" data-action="click->admin-faqs#edit">
      <td><button type="button" class="fq-question" aria-label="Edit question: ${escape(text(question.question))}" title="${escape(text(question.question))}">${escape(text(question.question))}</button><div class="fq-excerpt">${escape(text(question.answer))}</div></td>
      <td><span class="fq-category-name" title="${escape(this.categoryName(question.category_id))}">${escape(this.categoryName(question.category_id))}</span></td>
      <td class="fq-updated">${escape(date(question.last_updated))}</td>
      <td><button type="button" class="fq-switch" role="switch" aria-checked="${question.is_visible}" aria-label="Show on help page: ${escape(text(question.question))}" data-id="${question.id}" data-action="click->admin-faqs#toggleVisibility" ${this.busy ? 'disabled' : ''}></button></td></tr>`).join('') || `<tr><td colspan="4" class="fq-empty">${query ? 'No questions match your search.' : 'No questions here yet.'}</td></tr>`
  }

  categoryName(id) { return this.categories.find(category => category.id === Number(id))?.name || 'Unassigned' }
  categoryCount(id) { return this.questions.filter(question => question.category_id === Number(id)).length }
  filterCategory(event) { this.activeCategory = event.currentTarget.dataset.category; this.render() }
  filterTab(event) { this.activeTab = event.currentTarget.dataset.tab; this.render() }
  field(name) { return this.formTarget.elements.namedItem(name) }

  newQuestion() {
    if (this.busy || !this.loaded) return
    if (!this.categories.length) { this.openCategories(); this.showError(this.categoryNoticeTarget, new Error('Add a category before adding your first question.')); return }
    this.open()
  }

  edit(event) {
    if (this.busy || event.target.closest('[role="switch"]')) return
    const question = this.questions.find(item => item.id === Number(event.currentTarget.dataset.id))
    if (question) this.open(question)
  }

  open(question = null, draft = null) {
    this.question = question
    const source = question || draft || { category_id: this.activeCategory || this.categories[0]?.id, is_visible: true }
    this.formTarget.reset()
    this.field('category_id').innerHTML = this.categories.map(category => `<option value="${category.id}">${escape(category.name)}</option>`).join('')
    for (const name of ['question', 'answer', 'category_id', 'title', 'description']) this.field(name).value = source[name] ?? ''
    this.field('question').value = text(source.question)
    this.field('is_visible').checked = source.is_visible
    this.titleTarget.textContent = question ? 'Edit question' : 'Add a question'
    this.kickerTarget.textContent = question ? `Question #${question.id} · ${this.categoryName(question.category_id)}` : 'New question'
    this.saveButtonTarget.textContent = question ? 'Save changes' : 'Add question'
    this.deleteButtonTarget.hidden = !question
    this.duplicateButtonTarget.hidden = !question
    this.drawerNoticeTarget.hidden = true
    this.metaTarget.hidden = !question
    this.metaTarget.textContent = question ? `ID ${question.id} · Last updated ${date(question.last_updated)}` : ''
    this.metadataTarget.open = Boolean(source.title || source.description)
    this.normalFooterTarget.hidden = false
    this.deleteConfirmTarget.hidden = true
    this.formChanged()
    if (!this.dialogTarget.open) this.dialogTarget.showModal()
    this.dialogTarget.querySelector('.fq-drawer-body').scrollTop = 0
    this.field('question').focus()
    this.render()
  }

  formChanged() {
    const words = text(this.field('answer').value).trim()
    this.wordCountTarget.textContent = `${words ? words.split(/\s+/).length : 0} words`
    this.visibilityLabelTarget.textContent = this.field('is_visible').checked ? 'Visible' : 'Hidden'
    for (const [name, target, suggestion] of [['title', this.titleCountTarget, 60], ['description', this.descriptionCountTarget, 160]]) {
      target.textContent = `${this.field(name).value.length} / ${suggestion}`
      target.classList.toggle('is-warning', this.field(name).value.length > suggestion)
    }
  }

  close(event) {
    event?.preventDefault()
    if (this.busy) return
    this.dialogTarget.close()
    this.question = null
    this.render()
  }

  backdrop(event) { if (this.outsideDialog(event, this.dialogTarget)) this.close(event) }
  categoryBackdrop(event) { if (this.outsideDialog(event, this.categoryDialogTarget)) this.closeCategories(event) }
  outsideDialog(event, dialog) {
    if (event.target !== dialog) return false
    const box = dialog.getBoundingClientRect()
    return event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom
  }

  duplicate() {
    if (this.busy || !this.question) return
    const draft = Object.fromEntries(new FormData(this.formTarget))
    draft.question = `${text(draft.question).slice(0, 248)} (copy)`
    draft.is_visible = false
    this.open(null, draft)
  }

  upsert(question) {
    const index = this.questions.findIndex(item => item.id === question.id)
    if (index === -1) this.questions.push(question)
    else this.questions[index] = question
  }

  async mutate(url, method, body, notice, success) {
    if (this.busy) return
    this.busy = true
    notice.hidden = true
    const controls = [...this.element.querySelectorAll('button, input, textarea, select')].map(control => [control, control.disabled])
    controls.forEach(([control]) => { control.disabled = true })
    try {
      const data = await this.request(url, { method, body: JSON.stringify(body) })
      if (!this.connected) return
      success(data)
    } catch (error) {
      if (this.connected) this.showError(notice, error instanceof TypeError ? new Error('Connection interrupted. Refresh to check whether the change was saved before retrying.') : error)
    } finally {
      this.busy = false
      controls.forEach(([control, disabled]) => { control.disabled = disabled })
      if (this.connected) this.render()
    }
  }

  save(event) {
    event.preventDefault()
    if (this.busy || !this.formTarget.reportValidity()) return
    const question = Object.fromEntries(new FormData(this.formTarget))
    question.is_visible = this.field('is_visible').checked
    const existing = this.question
    if (existing) question.version = existing.version
    this.mutate(existing ? `${this.baseValue}/${existing.id}` : this.baseValue, existing ? 'PATCH' : 'POST', { question }, this.drawerNoticeTarget, data => {
      this.upsert(data)
      this.dialogTarget.close()
      this.question = null
      this.toast(existing ? 'Changes saved' : data.is_visible ? 'Question added and live' : 'Question added as hidden')
    })
  }

  toggleVisibility(event) {
    event.stopPropagation()
    if (this.busy) return
    const question = this.questions.find(item => item.id === Number(event.currentTarget.dataset.id))
    if (!question) return
    const previous = question.is_visible
    this.mutate(`${this.baseValue}/${question.id}`, 'PATCH', { question: { is_visible: !previous, version: question.version } }, this.noticeTarget, data => {
      this.upsert(data)
      this.toast(data.is_visible ? 'Question is visible on the help page' : 'Question hidden from the help page', () => {
        this.mutate(`${this.baseValue}/${data.id}`, 'PATCH', { question: { is_visible: previous, version: data.version } }, this.noticeTarget, restored => { this.upsert(restored); this.toast('Visibility restored') })
      })
    })
  }

  askDelete() { this.normalFooterTarget.hidden = true; this.deleteConfirmTarget.hidden = false; this.deleteConfirmTarget.querySelector('button').focus() }
  cancelDelete() { this.normalFooterTarget.hidden = false; this.deleteConfirmTarget.hidden = true; this.deleteButtonTarget.focus() }
  confirmDelete() {
    if (!this.question) return
    const question = this.question
    this.mutate(`${this.baseValue}/${question.id}`, 'DELETE', { version: question.version }, this.drawerNoticeTarget, data => {
      this.questions = this.questions.filter(item => item.id !== question.id)
      this.dialogTarget.close()
      this.question = null
      this.toast('Question deleted', () => this.mutate(`${this.baseValue}/restore`, 'POST', { token: data.undo_token }, this.noticeTarget, restored => { this.upsert(restored); this.toast('Question restored') }))
    })
  }

  openCategories() {
    if (this.busy || !this.loaded) return
    this.categoryNoticeTarget.hidden = true
    this.renderCategories()
    this.categoryDialogTarget.showModal()
  }

  renderCategories() {
    this.categoryRowsTarget.innerHTML = this.categories.map(category => {
      const count = this.categoryCount(category.id)
      return `<div class="fq-category-row"><input value="${escape(category.name)}" maxlength="255" data-id="${category.id}" aria-label="Category name: ${escape(category.name)}" data-action="change->admin-faqs#renameCategory keydown.enter->admin-faqs#renameCategory"><small>${number(count)} ${count === 1 ? 'question' : 'questions'}</small><button type="button" class="fq-icon" aria-label="Delete category: ${escape(category.name)}" title="${count ? 'Move or delete its questions first' : 'Delete empty category'}" data-id="${category.id}" data-action="admin-faqs#deleteCategory" ${count ? 'disabled' : ''}><span class="material-symbols-rounded" aria-hidden="true">delete</span></button></div>`
    }).join('')
  }

  closeCategories(event) { event?.preventDefault(); if (!this.busy) this.categoryDialogTarget.close() }

  addCategory(event) {
    event.preventDefault()
    const name = this.newCategoryTarget.value
    this.mutate(this.categoriesUrlValue, 'POST', { category: { name } }, this.categoryNoticeTarget, data => { this.setData(data); this.renderCategories(); this.newCategoryTarget.value = '' })
  }

  renameCategory(event) {
    event.preventDefault()
    const category = this.categories.find(item => item.id === Number(event.currentTarget.dataset.id))
    const name = event.currentTarget.value
    if (!category || category.name === name) return
    this.mutate(`${this.categoriesUrlValue}/${category.id}`, 'PATCH', { category: { name, version: category.version } }, this.categoryNoticeTarget, data => { this.setData(data); this.renderCategories() })
  }

  deleteCategory(event) {
    const category = this.categories.find(item => item.id === Number(event.currentTarget.dataset.id))
    if (!category) return
    this.mutate(`${this.categoriesUrlValue}/${category.id}`, 'DELETE', { version: category.version }, this.categoryNoticeTarget, data => { this.setData(data); this.renderCategories() })
  }

  showError(target, error) { target.textContent = error.message; target.hidden = false; target.scrollIntoView({ block: 'nearest' }) }
  toast(message, undo = null) {
    clearTimeout(this.toastTimer)
    this.toastMessageTarget.textContent = message
    this.undoAction = undo
    this.undoButtonTarget.hidden = !undo
    this.toastTarget.hidden = false
    this.toastTimer = setTimeout(() => { this.toastTarget.hidden = true; this.undoAction = null }, 5000)
  }
  undo() { if (this.busy || !this.undoAction) return; const action = this.undoAction; this.undoAction = null; this.toastTarget.hidden = true; action() }
}
